import { OpenInNew } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { DataTableColumn, PaginatedProps, StandardListViewV2 } from '../../../Components/CoreLib/library';
import { triaxReferenceNumberColumnKeySuffixes } from '../../../constants/triaxReferenceNumberColumnKeySuffixes';
import { OrderDto, QuoteDto } from '../../../dtos';
import { useGetOrderQuery } from '../../../store/generated/generatedApi';
import { formatDate } from '../../../util';

export interface ILineItemOrdersDialogProps {
    open: boolean;
    onClose: () => void;
    lineItemId: string;
    quote: QuoteDto;
}

const defaultPaginationProps: PaginatedProps = {
    sorting: [{ key: 'ORDER_DATE', direction: 'desc' }, { key: 'ORDER_NUMBER', direction: 'desc' }],
    page: 0,
    pageSize: 25,
};

export const LineItemOrdersDialog: FC<ILineItemOrdersDialogProps> = ({ open, onClose, lineItemId, quote }) => {
    const quoteLabel = quote.appraisalType === 0 ? 'Quote #' : 'Estimate #';

    const tableColumns: DataTableColumn<OrderDto>[] = useMemo(
        () => [
            { key: 'orderNumber', label: 'Order #', sortKey: 'ORDER_NUMBER' },
            {
                key: 'orderDate',
                label: 'Order Date',
                sortKey: 'ORDER_DATE',
                fieldMapper: (row) => (row.orderDate ? formatDate(row.orderDate) : ''),
            },
            { key: 'orderer', label: 'Orderer', sortKey: 'ORDERER', fieldMapper: (row) => row.orderer || '' },
            { key: 'orderLineItemCount', label: 'Line Items Count', sortKey: 'LINE_ITEM_COUNT' },
            {
                key: 'quoteNumber',
                label: quoteLabel,
                sortKey: 'QUOTE_NUMBER',
                fieldMapper: (row) => (
                    <Typography>
                        <Link
                            href={`/quote/${row.quoteId}`}
                            variant='body2'
                            onClick={(e) => {
                                e.preventDefault();
                                window.open(`/quote/${row.quoteId}`, '_blank');
                            }}>
                            {row.quote?.quoteNumber}
                        </Link>
                    </Typography>
                ),
            },
            {
                key: 'status',
                label: 'Status',
                sortKey: 'STATUS',
                unsorted: true,
                fieldMapper: (row) => (
                    <Typography>
                        {!!row.orderReviewId ? (
                            <Link
                                href={`/job/${row.jobId}/order/${row.id}/orderReview/${row.orderReviewId}`}
                                variant='body2'
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.open(`/job/${row.jobId}/order/${row.id}/orderReview/${row.orderReviewId}`, '_blank');
                                }}>
                                {row.status}
                            </Link>
                        ) : (
                            <>{row.status}</>
                        )}
                    </Typography>
                ),
            },
            {
                key: 'createdOn',
                label: 'Created On',
                sortKey: 'CREATED_ON',
                fieldMapper: (row) => (row.createdOn ? formatDate(row.createdOn, 'Pp') : ''),
            },
            {
                key: 'openInNew',
                label: '',
                sortKey: '',
                unsorted: true,
                fieldMapper: (row) => (
                    <Tooltip title='Open order in new tab'>
                        <IconButton
                            size='small'
                            onClick={() => window.open(`/job/${row.jobId}/order/${row.id}`, '_blank')}>
                            <OpenInNew fontSize='small' />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [quoteLabel]
    );

    return (
        <Dialog onClose={onClose} open={open} maxWidth='lg' fullWidth>
            <DialogTitle>
                <Typography variant='h2'>Orders</Typography>
            </DialogTitle>
            <DialogContent>
                <StandardListViewV2
                    referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                    enableMultiSort
                    headerIcon={<></>}
                    headerTitle={''}
                    breadCrumbs={[]}
                    defaultPaginationProps={defaultPaginationProps}
                    getDataQuery={useGetOrderQuery}
                    isShowActiveToggleVisible={false}
                    tableColumns={tableColumns}
                    entityNameSingular='Order'
                    permissionName='order'
                    additionalQueryParameters={{ lineItemId }}
                    disableGutters
                    disableSelection
                    hideMenu
                    paginationOnTop={true}
                    stickyTableHeader={true}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};
